import './XPAwardSummary.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.8 (2026-06-06) — XPAwardSummary.
 *
 * Pure-presentation render of a `force_xp_award` dispatch action.
 * End-of-session XP tally per Ch 10. Shows rank × outcome × role-play
 * multipliers. Resolved base + awarded XP surface via optional props.
 *
 * Schema 1-to-1 with the V9.1.c tool args at routes/dispatch.py:762-806:
 *   - rank: enum {Rookie, Agent, "00"} (required)
 *   - mission_outcome: enum {success, failure, incomplete} (required)
 *   - role_play_multiplier: number 0.5-1.5 (optional, default 1.0)
 *   - rationale: GM voice-line ≤30 words (optional)
 *
 * Canonical multipliers per md L3344-L3360:
 *   Rank:    Rookie ×0.75, Agent ×1.0, "00" ×2.0
 *   Outcome: success ×2, failure ×0.25, incomplete ×1
 */

export type PcRank = 'Rookie' | 'Agent' | '00';
export type MissionOutcome = 'success' | 'failure' | 'incomplete';

export interface XPAwardSummaryProps {
  rank: PcRank;
  missionOutcome: MissionOutcome;
  /** Role-play quality multiplier 0.5-1.5. Default 1.0. */
  rolePlayMultiplier?: number;
  rationale?: string;
  /** Resolved base XP before multipliers (optional). */
  resolvedBaseXp?: number | null;
  /** Resolved final XP awarded. Omit → pending state. */
  resolvedAwardedXp?: number | null;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

const RANK_MULTIPLIER: Record<PcRank, number> = {
  Rookie: 0.75,
  Agent: 1.0,
  '00': 2.0,
};

const OUTCOME_MULTIPLIER: Record<MissionOutcome, number> = {
  success: 2.0,
  failure: 0.25,
  incomplete: 1.0,
};

const OUTCOME_LABEL: Record<MissionOutcome, string> = {
  success: 'Success',
  failure: 'Failure',
  incomplete: 'Incomplete',
};

function formatMultiplier(m: number): string {
  if (Number.isInteger(m)) return `×${m}`;
  return `×${m.toFixed(2).replace(/\.?0+$/, '')}`;
}

export function XPAwardSummary({
  rank,
  missionOutcome,
  rolePlayMultiplier = 1.0,
  rationale,
  resolvedBaseXp,
  resolvedAwardedXp,
  onDismiss,
  testidPrefix = 'br-xp-award',
}: XPAwardSummaryProps) {
  const rankMul = RANK_MULTIPLIER[rank];
  const outcomeMul = OUTCOME_MULTIPLIER[missionOutcome];
  const rpMul = rolePlayMultiplier;
  const hasAwarded = typeof resolvedAwardedXp === 'number' && resolvedAwardedXp >= 0;
  const hasBase = typeof resolvedBaseXp === 'number' && resolvedBaseXp >= 0;

  return (
    <aside
      className="br-xp-award"
      data-testid={testidPrefix}
      data-rank={rank}
      data-outcome={missionOutcome}
      data-state={hasAwarded ? 'resolved' : 'pending'}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-xp-award-header">
        <span className="br-xp-award-label">End-of-Session XP</span>
        <span
          className="br-xp-award-rank-chip"
          data-testid={`${testidPrefix}-rank-chip`}
          data-rank-id={rank}
        >
          {rank}
        </span>
      </header>

      <div
        className="br-xp-award-multipliers"
        data-testid={`${testidPrefix}-multipliers`}
      >
        <div
          className="br-xp-award-multiplier-row"
          data-testid={`${testidPrefix}-mult-rank`}
        >
          <span className="br-xp-award-multiplier-key">Rank</span>
          <span className="br-xp-award-multiplier-val br-label">{formatMultiplier(rankMul)}</span>
        </div>
        <div
          className="br-xp-award-multiplier-row"
          data-testid={`${testidPrefix}-mult-outcome`}
          data-outcome-id={missionOutcome}
        >
          <span className="br-xp-award-multiplier-key">
            Outcome: {OUTCOME_LABEL[missionOutcome]}
          </span>
          <span className="br-xp-award-multiplier-val br-label">
            {formatMultiplier(outcomeMul)}
          </span>
        </div>
        <div
          className="br-xp-award-multiplier-row"
          data-testid={`${testidPrefix}-mult-roleplay`}
        >
          <span className="br-xp-award-multiplier-key">Role-play</span>
          <span className="br-xp-award-multiplier-val br-label">{formatMultiplier(rpMul)}</span>
        </div>
      </div>

      {hasAwarded ? (
        <div
          className="br-xp-award-resolved"
          data-testid={`${testidPrefix}-resolved`}
        >
          {hasBase && (
            <div className="br-xp-award-base-row">
              <span className="br-xp-award-base-label">Base XP</span>
              <span
                className="br-xp-award-base-value br-label"
                data-testid={`${testidPrefix}-base-xp`}
              >
                {resolvedBaseXp}
              </span>
            </div>
          )}
          <div className="br-xp-award-final-row">
            <span className="br-xp-award-final-label">Awarded</span>
            <span
              className="br-xp-award-final-value br-label"
              data-testid={`${testidPrefix}-awarded-xp`}
            >
              {resolvedAwardedXp} XP
            </span>
          </div>
        </div>
      ) : (
        <p
          className="br-xp-award-pending"
          data-testid={`${testidPrefix}-pending`}
        >
          Awaiting XP tally…
        </p>
      )}

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-xp-award-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}
    </aside>
  );
}
